export const getArrowStyles = placement => {
  switch (placement) {
    case 'top':
      return {
        borderTop: '0.75em solid currentColor',
        bottom: '-1.25em',
        left: '50%',
        transform: `translate3d(-50%, 0, 0)`
      };

    case 'right':
      return {
        borderRight: '0.75em solid currentColor',
        top: '50%',
        left: '-1.25em',
        transform: `translate3d(0, -50%, 0)`
      };

    case 'bottom':
      return {
        borderBottom: '0.75em solid currentColor',
        top: '-1.25em',
        left: '50%',
        transform: `translate3d(-50%, 0, 0)`
      };

    case 'left':
      return {
        borderLeft: '0.75em solid currentColor',
        top: '50%',
        right: '-1.25em',
        transform: `translate3d(0, -50%, 0)`
      };

    default:
      {
        const unsupported = placement;
        throw new Error(`Tooltip placement '${unsupported}' is not supported.`);
      }
  }
};
/** Get the offset relative to document body. */

const getOffsetBoundingClientRect = (element, fixedContainer = false) => {
  const {
    width,
    height,
    top,
    left
  } = element.getBoundingClientRect();
  const {
    top: bodyTop,
    left: bodyLeft
  } = document.body.getBoundingClientRect();

  if (fixedContainer) {
    return {
      width,
      height,
      top: top - bodyTop - window.scrollY,
      left: left - bodyLeft - window.scrollX,
      bottom: top - bodyTop - window.scrollY + height,
      right: left - bodyLeft - window.scrollX + width
    };
  }

  return {
    width,
    height,
    top: top - bodyTop,
    left: left - bodyLeft,
    bottom: top - bodyTop + height,
    right: left - bodyLeft + width
  };
};

export const getTooltipTransitionProps = (containerRef, placement, fixed = false) => {
  const container = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
  if (!container) return null;
  const {
    top,
    bottom,
    left,
    right,
    width,
    height
  } = getOffsetBoundingClientRect(container, fixed);

  switch (placement) {
    case 'top':
      {
        const baseStyles = {
          top: `${top}px`,
          left: `${left + width / 2}px`,
          // transformOrigin: 'bottom',
          transformOrigin: 'bottom'
        };
        return {
          from: { ...baseStyles,
            transform: `translate3d(-50%, calc(-100% - 0.5em), 0) rotateX(-20deg)`,
            opacity: 0.5
          },
          enter: { ...baseStyles,
            transform: `translate3d(-50%, calc(-100% - 1em), 0) rotateX(0deg)`,
            opacity: 1,
            pointerEvents: 'none'
          },
          leave: { ...baseStyles,
            transform: `translate3d(-50%, calc(-100% - 0.5em), 0)  rotateX(-20deg)`,
            opacity: 0
          }
        };
      }

    case 'right':
      {
        const baseStyles = {
          left: `${right}px`,
          top: `${top + height / 2}px`,
          transform: `translate3d(1em, -50%, 0)`,
          transformOrigin: 'left'
        };
        return {
          from: { ...baseStyles,
            transform: `translate3d(0.5em, -50%, 0) rotateY(-20deg)`,
            opacity: 0.5
          },
          enter: { ...baseStyles,
            transform: `translate3d(1em, -50%, 0) rotateY(0deg)`,
            opacity: 1,
            pointerEvents: 'none'
          },
          leave: { ...baseStyles,
            transform: `translate3d(0.5em, -50%, 0) rotateY(-20deg)`,
            opacity: 0
          }
        };
      }

    case 'bottom':
      {
        const baseStyles = {
          top: `${bottom}px`,
          left: `${left + width / 2}px`,
          transform: `translate3d(-50%, 1em, 0)`,
          transformOrigin: 'top'
        };
        return {
          from: { ...baseStyles,
            transform: `translate3d(-50%, 0.5em, 0) rotateX(-20deg)`,
            opacity: 0.5
          },
          enter: { ...baseStyles,
            transform: `translate3d(-50%, 1em, 0) rotateX(0deg)`,
            opacity: 1
          },
          leave: { ...baseStyles,
            transform: `translate3d(-50%, 0.5em, 0) rotateX(-20deg)`,
            opacity: 0
          }
        };
      }

    case 'left':
      {
        const baseStyles = {
          left: `${left}px`,
          top: `${top + height / 2}px`,
          transform: `translate3d(calc(-100% - 1em), -50%, 0)`,
          transformOrigin: 'right'
        };
        return {
          from: { ...baseStyles,
            transform: `translate3d(calc(-100% - 0.5em), -50%, 0) rotateY(-20deg)`,
            opacity: 0.5
          },
          enter: { ...baseStyles,
            transform: `translate3d(calc(-100% - 1em), -50%, 0) rotateY(0deg)`,
            opacity: 1,
            pointerEvents: 'none'
          },
          leave: { ...baseStyles,
            transform: `translate3d(calc(-100% - 0.5em), -50%, 0) rotateY(-20deg)`,
            opacity: 0
          }
        };
      }

    default:
      {
        const unsupported = placement;
        throw new Error(`Tooltip placement '${unsupported}' is not supported.`);
      }
  }
};
export const mapTriggerEvents = ({
  trigger,
  display,
  hide,
  toggle,
  childProps
}) => {
  const triggers = Array.isArray(trigger) ? trigger : [trigger];

  const mapTriggerEvent = triggerEvent => {
    const MOUSE_POINTER_TYPE = 'mouse';

    switch (triggerEvent) {
      case 'hover':
        return {
          onPointerEnter: event => {
            var _childProps$onPointer;

            (_childProps$onPointer = childProps.onPointerEnter) === null || _childProps$onPointer === void 0 ? void 0 : _childProps$onPointer.call(childProps, event);

            if (event.pointerType === MOUSE_POINTER_TYPE) {
              display();
            }
          },
          onPointerLeave: event => {
            var _childProps$onPointer2;

            (_childProps$onPointer2 = childProps.onPointerLeave) === null || _childProps$onPointer2 === void 0 ? void 0 : _childProps$onPointer2.call(childProps, event);

            if (event.pointerType === MOUSE_POINTER_TYPE) {
              hide();
            }
          }
        };

      case 'focus':
        return {
          onFocus: event => {
            var _childProps$onFocus;

            (_childProps$onFocus = childProps.onFocus) === null || _childProps$onFocus === void 0 ? void 0 : _childProps$onFocus.call(childProps, event);
            display();
          },
          onBlur: event => {
            var _childProps$onBlur;

            (_childProps$onBlur = childProps.onBlur) === null || _childProps$onBlur === void 0 ? void 0 : _childProps$onBlur.call(childProps, event);
            hide();
          }
        };

      case 'touch':
        return {
          onTouchStart: event => {
            var _childProps$onTouchSt;

            (_childProps$onTouchSt = childProps.onTouchStart) === null || _childProps$onTouchSt === void 0 ? void 0 : _childProps$onTouchSt.call(childProps, event);
            toggle();
          }
        };

      default:
        {
          const unsupported = triggerEvent;
          throw new Error(`Unsupported trigger event type: ${unsupported}`);
        }
    }
  };

  return triggers.reduce((handlers, triggerEvent) => ({ ...handlers,
    ...mapTriggerEvent(triggerEvent)
  }), {});
};